import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { fetchAllPages, dateRangeParams, formatExportDate } from '../../utils/exportData';
import {
  HiOutlinePlus, HiOutlineTrash, HiOutlinePencil, HiOutlineCheck,
  HiOutlinePause, HiOutlineExclamation, HiOutlineSpeakerphone,
  HiOutlineX
} from 'react-icons/hi';

const EMPTY_NOTICE_FILTERS = { search: '', status: '', type: '', audience: '', from: '', to: '' };
const noticeApiParams = ({ from, to, ...rest }) => ({ ...rest, ...dateRangeParams(from, to) });
const getNotices = (params) => api.get('/superadmin/notices', { params });

const NOTICE_EXPORT_COLUMNS = [
  { header: 'Title', value: (n) => n.title },
  { header: 'Message', value: (n) => n.message },
  { header: 'Type', value: (n) => n.type },
  { header: 'Priority', value: (n) => n.priority },
  { header: 'Status', value: (n) => (n.isActive ? 'Active' : 'Inactive') },
  { header: 'Audience', value: (n) => (n.targetOrganizations?.length ? `${n.targetOrganizations.length} organization(s)` : 'All organizations') },
  { header: 'Starts', value: (n) => formatExportDate(n.startsAt) },
  { header: 'Expires', value: (n) => formatExportDate(n.expiresAt) },
  { header: 'Created', value: (n) => formatExportDate(n.createdAt) },
];

const TYPE_STYLES = {
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
  urgent: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
};

const NoticeModal = ({ isOpen, onClose, notice, onSave }) => {
  const [formData, setFormData] = useState({
    title: notice?.title || '',
    message: notice?.message || '',
    type: notice?.type || 'info',
    priority: notice?.priority || 0,
    isActive: notice?.isActive !== undefined ? notice.isActive : true,
    startsAt: notice?.startsAt ? new Date(notice.startsAt).toISOString().slice(0, 16) : '',
    expiresAt: notice?.expiresAt ? new Date(notice.expiresAt).toISOString().slice(0, 16) : '',
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (notice) {
      setFormData({
        title: notice.title || '',
        message: notice.message || '',
        type: notice.type || 'info',
        priority: notice.priority || 0,
        isActive: notice.isActive !== undefined ? notice.isActive : true,
        startsAt: notice.startsAt ? new Date(notice.startsAt).toISOString().slice(0, 16) : '',
        expiresAt: notice.expiresAt ? new Date(notice.expiresAt).toISOString().slice(0, 16) : '',
      });
    } else {
      setFormData({
        title: '', message: '', type: 'info', priority: 0, isActive: true, startsAt: '', expiresAt: '',
      });
    }
    setErrors({});
  }, [notice, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(Object.values(newErrors)[0]);
      return;
    }

    const data = {
      ...formData,
      priority: parseInt(formData.priority) || 0,
      startsAt: formData.startsAt || null,
      expiresAt: formData.expiresAt || null,
    };
    onSave(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {notice ? 'Edit Notice' : 'Create Notice'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <HiOutlineX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => { setFormData({ ...formData, title: e.target.value }); setErrors({ ...errors, title: '' }); }}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="e.g., System Maintenance Notice"
            />
            {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => { setFormData({ ...formData, message: e.target.value }); setErrors({ ...errors, message: '' }); }}
              rows={3}
              className={`w-full border rounded-lg px-3 py-2 text-sm resize-none ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="The notice message to display in the marquee..."
            />
            {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
          </div>

          {/* Type & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="info">ℹ️ Info</option>
                <option value="warning">⚠️ Warning</option>
                <option value="success">✅ Success</option>
                <option value="urgent">🚨 Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="0"
              />
              <p className="text-xs text-gray-400 mt-1">Higher = shown first</p>
            </div>
          </div>

          {/* Scheduling */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Starts At</label>
              <input
                type="datetime-local"
                value={formData.startsAt}
                onChange={(e) => setFormData({ ...formData, startsAt: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Leave empty for immediate</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
              <input
                type="datetime-local"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Leave empty for never</p>
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
            <span className="text-sm text-gray-700">
              {formData.isActive ? 'Active — will be displayed' : 'Inactive — hidden from marquee'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              {notice ? 'Save Changes' : 'Create Notice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NoticesList = () => {
  const { superAdmin, isAuthenticated } = useSuperAdmin();
  const queryClient = useQueryClient();
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({ ...EMPTY_NOTICE_FILTERS, page: 1 });
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  const filtersActive = Object.keys(EMPTY_NOTICE_FILTERS).some((k) => filters[k]);

  // Fetch notices
  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-notices', filters],
    queryFn: async () => {
      const response = await getNotices(noticeApiParams(filters));
      return response.data;
    },
    enabled: isAuthenticated
  });

  const notices = data?.data || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  // Create notice
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/superadmin/notices', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-notices']);
      toast.success('Notice created successfully');
      setIsModalOpen(false);
      setSelectedNotice(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create notice');
    }
  });

  // Update notice
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/superadmin/notices/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-notices']);
      toast.success('Notice updated successfully');
      setIsModalOpen(false);
      setSelectedNotice(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update notice');
    }
  });

  // Toggle notice
  const toggleMutation = useMutation({
    mutationFn: (id) => api.patch(`/superadmin/notices/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-notices']);
      toast.success('Notice status updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to toggle notice');
    }
  });

  // Delete notice
  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/superadmin/notices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-notices']);
      toast.success('Notice deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete notice');
    }
  });

  const handleSave = (formData) => {
    if (selectedNotice) {
      updateMutation.mutate({ id: selectedNotice._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (notice) => {
    if (window.confirm(`Delete "${notice.title}"? This cannot be undone.`)) {
      deleteMutation.mutate(notice._id);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Notices</h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage announcements displayed in the admin panel marquee banner
          </p>
        </div>
        <button
          onClick={() => { setSelectedNotice(null); setIsModalOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <HiOutlinePlus className="w-4 h-4" />
          Add Notice
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search title or message..."
          value={filters.search}
          onChange={(e) => setFilter({ search: e.target.value })}
          className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <FilterSelect
          value={filters.status}
          onChange={(v) => setFilter({ status: v })}
          allLabel="All Status"
          options={[['active', 'Active'], ['inactive', 'Inactive']]}
        />
        <FilterSelect
          value={filters.type}
          onChange={(v) => setFilter({ type: v })}
          allLabel="All Types"
          options={[['info', 'Info'], ['warning', 'Warning'], ['success', 'Success'], ['urgent', 'Urgent']]}
        />
        <FilterSelect
          value={filters.audience}
          onChange={(v) => setFilter({ audience: v })}
          allLabel="All Audiences"
          options={[['all', 'All organizations'], ['targeted', 'Specific organizations']]}
        />
        <DateRangeFilter
          label="Created"
          from={filters.from}
          to={filters.to}
          onChange={({ from, to }) => setFilter({ from, to })}
        />
        <ResetFiltersButton active={filtersActive} onReset={() => setFilter(EMPTY_NOTICE_FILTERS)} />
        <ExportMenu
          fileName="notices"
          columns={NOTICE_EXPORT_COLUMNS}
          getRows={() => fetchAllPages(getNotices, noticeApiParams(filters))}
        />
        <span className="text-sm text-gray-500">{pagination.total} notice{pagination.total !== 1 ? 's' : ''}</span>
      </div>

      {/* Notices List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : notices.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <HiOutlineSpeakerphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-gray-700 font-medium mb-1">No notices yet</h4>
          <p className="text-sm text-gray-500 mb-4">Create a notice to display announcements in the admin panel marquee</p>
          <button
            onClick={() => { setSelectedNotice(null); setIsModalOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
          >
            <HiOutlinePlus className="w-4 h-4" />
            Create First Notice
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {notices.map((notice) => {
            const styles = TYPE_STYLES[notice.type] || TYPE_STYLES.info;
            return (
              <div
                key={notice._id}
                className={`bg-white rounded-xl border ${styles.border} p-4 transition-shadow hover:shadow-md`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles.badge}`}>
                        {notice.type}
                      </span>
                      {!notice.isActive && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                          Inactive
                        </span>
                      )}
                      <span className="text-xs text-gray-400">Priority: {notice.priority}</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 truncate">{notice.title}</h4>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notice.message}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span>Created: {formatDate(notice.createdAt)}</span>
                      {notice.startsAt && <span>Starts: {formatDate(notice.startsAt)}</span>}
                      {notice.expiresAt && <span>Expires: {formatDate(notice.expiresAt)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => toggleMutation.mutate(notice._id)}
                      className={`p-2 rounded-lg transition-colors ${
                        notice.isActive
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={notice.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {notice.isActive ? <HiOutlineCheck className="w-4 h-4" /> : <HiOutlinePause className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => { setSelectedNotice(notice); setIsModalOpen(true); }}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <HiOutlinePencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(notice)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <HiOutlineTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))}
            disabled={pagination.page <= 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setFilters(f => ({ ...f, page: Math.min(pagination.pages, f.page + 1) }))}
            disabled={pagination.page >= pagination.pages}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      <NoticeModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedNotice(null); }}
        notice={selectedNotice}
        onSave={handleSave}
      />
    </div>
  );
};

export default NoticesList;